import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../components/auth/AuthContext';
import { adminConfigService } from '../../services/AdminConfigService';
import { api } from '../../lib/api';
import PlacidTemplateImportModal from './PlacidTemplateImportModal';
import ModelFailuresPanel from './ModelFailuresPanel';
import {
  Settings,
  Image,
  Sparkles,
  Bot,
  Type,
  RefreshCw,
  Save,
  AlertTriangle,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  ToggleLeft,
  ToggleRight,
  Loader2,
  Shield,
  Zap,
  Brain,
  Palette,
  FileText,
  Users,
  Plus,
  Trash2,
  RotateCw,
  Activity,
  FlaskConical
} from 'lucide-react';
import UsersManagement from './UsersManagement';
import ImagePromptEditor from '../../components/admin/ImagePromptEditor';

const AdminConfigPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // Estados principais
  const [activeTab, setActiveTab] = useState('users');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Dados das configurações
  const [generators, setGenerators] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [placidSettings, setPlacidSettings] = useState({});
  const [modelSelector, setModelSelector] = useState({});
  const [agentConfig, setAgentConfig] = useState({});
  const [fonts, setFonts] = useState({});
  
  // 🆕 Estado do modal de importação
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [syncingTemplateId, setSyncingTemplateId] = useState(null);

  // Creatomate Split
  const [creatomateSplit, setCreatomateSplit] = useState({
    enabled: false,
    creatomate_count: 0,
    placid_count: 3,
    templated_count: 0
  });

  // ✅ Fase 4: Image Model Provider toggle (Azure vs OpenAI gpt-image)
  const [imageModelProvider, setImageModelProvider] = useState({
    provider: 'auto',
    gpt_image_model_override: 'auto',
  });
  const [isSavingImageProvider, setIsSavingImageProvider] = useState(false);

  // Lab Agents
  const [labLayoutAgents, setLabLayoutAgents] = useState({});
  const [labPlannerConfig, setLabPlannerConfig] = useState({});
  const [labLayoutSystemPrompt, setLabLayoutSystemPrompt] = useState('');
  const [labVariables, setLabVariables] = useState({});
  const [expandedLayout, setExpandedLayout] = useState(null);
  const [newLayoutForm, setNewLayoutForm] = useState(null);

  // Lab React Agents (provider react_render)
  const [labReactLayoutAgents, setLabReactLayoutAgents] = useState({});
  const [labReactSystemPrompt, setLabReactSystemPrompt] = useState('');
  const [expandedReactLayout, setExpandedReactLayout] = useState(null);
  const [newReactLayoutForm, setNewReactLayoutForm] = useState(null);

  // Lab Nichos
  const [labNiches, setLabNiches] = useState({});
  const [expandedNiche, setExpandedNiche] = useState(null);
  const [newNicheForm, setNewNicheForm] = useState(null);
  const [regeneratingNicheId, setRegeneratingNicheId] = useState(null);

  // Templated Templates
  const [templatedTemplates, setTemplatedTemplates] = useState({});
  const [availableTemplatedTemplates, setAvailableTemplatedTemplates] = useState([]);
  const [isLoadingTemplated, setIsLoadingTemplated] = useState(false);

  // Estados de UI
  const [expandedSections, setExpandedSections] = useState({
    generators: true,
    templates: false,
    modelSelector: false,
    agent: false,
    fonts: false
  });

  // Verificar se é admin
  useEffect(() => {
    if (!currentUser?.is_admin) {
      navigate('/contents');
    }
  }, [currentUser, navigate]);

  // Carregar dados
  const loadAllData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const [
        generatorsRes,
        templatesRes,
        placidRes,
        modelSelectorRes,
        agentRes,
        fontsRes,
        labAgentsRes,
        labPlannerRes,
        labSystemPromptRes,
        labVarsRes,
        creatomateSplitRes,
        templatedTemplatesRes,
        labNichesRes,
        labReactAgentsRes,
        labReactSystemPromptRes
      ] = await Promise.all([
        adminConfigService.getImageGenerators(),
        adminConfigService.getPlacidTemplates(),
        adminConfigService.getPlacidSettings(),
        adminConfigService.getModelSelectorConfig(),
        adminConfigService.getAgentConfig(),
        adminConfigService.getPopularFonts(),
        adminConfigService.getLabLayoutAgents().catch(() => ({ layout_agents: {} })),
        adminConfigService.getLabPlannerConfig().catch(() => ({ planner_config: {} })),
        adminConfigService.getLabLayoutSystemPrompt().catch(() => ({ system_prompt: '' })),
        adminConfigService.getLabVariables().catch(() => ({})),
        adminConfigService.getCreatomateSplit().catch(() => ({ data: { enabled: false, creatomate_count: 0, placid_count: 3, templated_count: 0 } })),
        adminConfigService.getTemplatedTemplates().catch(() => ({ data: {} })),
        adminConfigService.getLabNiches().catch(() => ({ niches: {} })),
        adminConfigService.getLabReactLayoutAgents().catch(() => ({ layout_agents: {} })),
        adminConfigService.getLabReactSystemPrompt().catch(() => ({ system_prompt: '' }))
      ]);

      setGenerators(generatorsRes.data || []);
      setTemplates(templatesRes.data || []);
      setPlacidSettings(placidRes.data || {});
      setModelSelector(modelSelectorRes.data || {});
      setAgentConfig(agentRes.data || {});
      setFonts(fontsRes.data || {});
      setLabLayoutAgents(labAgentsRes.layout_agents || {});
      setLabPlannerConfig(labPlannerRes.planner_config || {});
      setLabLayoutSystemPrompt(labSystemPromptRes.system_prompt || '');
      setLabVariables(labVarsRes || {});
      setCreatomateSplit(creatomateSplitRes.data || { enabled: false, creatomate_count: 0, placid_count: 3, templated_count: 0 });
      setTemplatedTemplates(templatedTemplatesRes.data || {});
      setLabNiches(labNichesRes.niches || {});
      setLabReactLayoutAgents(labReactAgentsRes.layout_agents || {});
      setLabReactSystemPrompt(labReactSystemPromptRes.system_prompt || '');

      // ✅ Fase 4: load image model provider toggle
      try {
        const imgRes = await api.get('/api/admin/config/image-model-provider');
        if (imgRes.data?.data) {
          setImageModelProvider({
            provider: imgRes.data.data.provider || 'auto',
            gpt_image_model_override: imgRes.data.data.gpt_image_model_override || 'auto',
          });
        }
      } catch (e) {
        console.warn('Falha ao carregar image_model_provider:', e?.message);
      }

    } catch (err) {
      console.error('Erro ao carregar configurações:', err);
      setError('Erro ao carregar configurações. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // Mostrar mensagem de sucesso temporária
  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // Toggle seção expandida
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // ============================================
  // HANDLERS - GENERATORS
  // ============================================

  const handleToggleGenerator = async (generatorId, currentEnabled) => {
    try {
      await adminConfigService.toggleImageGenerator(generatorId, !currentEnabled);
      setGenerators(prev => 
        prev.map(g => g.id === generatorId ? { ...g, enabled: !currentEnabled } : g)
      );
      showSuccess(`Gerador ${!currentEnabled ? 'ativado' : 'desativado'} com sucesso!`);
    } catch (err) {
      setError('Erro ao alterar status do gerador');
    }
  };

  const handleReorderGenerators = async (fromIndex, toIndex) => {
    const newGenerators = [...generators];
    const [removed] = newGenerators.splice(fromIndex, 1);
    newGenerators.splice(toIndex, 0, removed);
    
    const updatedGenerators = newGenerators.map((g, i) => ({ ...g, order: i + 1 }));
    setGenerators(updatedGenerators);
    
    try {
      const orderList = updatedGenerators.map(g => ({ id: g.id, order: g.order }));
      await adminConfigService.reorderGenerators(orderList);
      showSuccess('Ordem dos geradores atualizada!');
    } catch (err) {
      setError('Erro ao reordenar geradores');
      loadAllData();
    }
  };

  // ============================================
  // HANDLERS - TEMPLATES
  // ============================================

  const handleToggleTemplate = async (templateId, currentEnabled) => {
    try {
      await adminConfigService.togglePlacidTemplate(templateId, !currentEnabled);
      setTemplates(prev => 
        prev.map(t => t.id === templateId ? { ...t, enabled: !currentEnabled } : t)
      );
      showSuccess(`Template ${!currentEnabled ? 'ativado' : 'desativado'}!`);
    } catch (err) {
      setError('Erro ao alterar status do template');
    }
  };

  const handleDeleteTemplate = async (templateId, templateTitle) => {
    if (!window.confirm(`Tem certeza que deseja remover o template "${templateTitle}"?\n\nEle poderá ser importado novamente do Placid depois.`)) {
      return;
    }
    
    try {
      await adminConfigService.deletePlacidTemplate(templateId);
      setTemplates(prev => prev.filter(t => t.id !== templateId));
      showSuccess(`Template "${templateTitle}" removido!`);
    } catch (err) {
      setError(`Erro ao remover template: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleSyncLayers = async (templateId) => {
    setSyncingTemplateId(templateId);
    
    try {
      const response = await adminConfigService.syncTemplateLayers(templateId);
      const syncInfo = response.sync_info;
      
      const templatesRes = await adminConfigService.getPlacidTemplates();
      setTemplates(templatesRes.data || []);
      
      let message = 'Layers sincronizados!';
      if (syncInfo) {
        const parts = [];
        if (syncInfo.layers_added?.length > 0) parts.push(`${syncInfo.layers_added.length} adicionados`);
        if (syncInfo.layers_removed?.length > 0) parts.push(`${syncInfo.layers_removed.length} removidos`);
        if (parts.length > 0) message += ` (${parts.join(', ')})`;
        else message += ' Nenhuma alteração detectada.';
      }
      
      showSuccess(message);
    } catch (err) {
      setError(`Erro ao sincronizar: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSyncingTemplateId(null);
    }
  };

  const handleTemplateImported = async () => {
    const templatesRes = await adminConfigService.getPlacidTemplates();
    setTemplates(templatesRes.data || []);
    showSuccess('Template importado com sucesso!');
  };

  // ============================================
  // HANDLERS - MODEL SELECTOR
  // ============================================

  const handleToggleModelSelector = async () => {
    try {
      const newEnabled = !modelSelector.enabled;
      await adminConfigService.updateModelSelectorConfig({ enabled: newEnabled });
      setModelSelector(prev => ({ ...prev, enabled: newEnabled }));
      showSuccess(`Seletor de modelo por IA ${newEnabled ? 'ativado' : 'desativado'}!`);
    } catch (err) {
      setError('Erro ao alterar seletor de modelo');
    }
  };

  const handleSaveModelSelectorConfig = async () => {
    setIsSaving(true);
    try {
      await adminConfigService.updateModelSelectorConfig({
        temperature: modelSelector.agent_config?.temperature,
        max_tokens: modelSelector.agent_config?.max_tokens,
        system_prompt: modelSelector.system_prompt
      });
      showSuccess('Configurações do seletor salvas!');
    } catch (err) {
      setError('Erro ao salvar configurações do seletor');
    } finally {
      setIsSaving(false);
    }
  };

  // ============================================
  // HANDLERS - AGENT CONFIG
  // ============================================

  const handleSaveAgentConfig = async () => {
    setIsSaving(true);
    try {
      await adminConfigService.updateAgentParams({
        temperature: agentConfig.agent_config?.temperature,
        max_tokens: agentConfig.agent_config?.max_tokens,
        max_iterations: agentConfig.agent_config?.max_iterations
      });
      showSuccess('Parâmetros do agente salvos!');
    } catch (err) {
      setError('Erro ao salvar parâmetros do agente');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAgentPrompts = async () => {
    setIsSaving(true);
    try {
      await adminConfigService.updateAgentPrompts({
        system_prompt: agentConfig.agent_system_prompt,
        user_prompt_template: agentConfig.agent_user_prompt_template
      });
      showSuccess('Prompts do agente salvos!');
    } catch (err) {
      setError('Erro ao salvar prompts do agente');
    } finally {
      setIsSaving(false);
    }
  };

  // ============================================
  // HANDLERS - LAB AGENTS
  // ============================================

  const handleSaveLabPlanner = async () => {
    setIsSaving(true);
    try {
      await adminConfigService.updateLabPlannerConfig(labPlannerConfig);
      showSuccess('Planner atualizado com sucesso!');
    } catch (err) {
      setError('Erro ao salvar planner: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveLabSystemPrompt = async () => {
    setIsSaving(true);
    try {
      await adminConfigService.updateLabLayoutSystemPrompt(labLayoutSystemPrompt);
      showSuccess('System prompt atualizado com sucesso!');
    } catch (err) {
      setError('Erro ao salvar system prompt: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveLayoutAgent = async (layoutId) => {
    setIsSaving(true);
    try {
      const agent = labLayoutAgents[layoutId];
      await adminConfigService.updateLabLayoutAgent(layoutId, agent);
      showSuccess(`Layout "${agent.name}" salvo com sucesso!`);
    } catch (err) {
      setError('Erro ao salvar layout: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleLayoutAgent = async (layoutId, currentEnabled) => {
    try {
      await adminConfigService.toggleLabLayoutAgent(layoutId, !currentEnabled);
      setLabLayoutAgents(prev => ({
        ...prev,
        [layoutId]: { ...prev[layoutId], enabled: !currentEnabled }
      }));
      showSuccess(`Layout ${!currentEnabled ? 'ativado' : 'desativado'}!`);
    } catch (err) {
      setError('Erro ao alterar status do layout');
    }
  };

  const handleDeleteLayoutAgent = async (layoutId) => {
    const agent = labLayoutAgents[layoutId];
    if (!window.confirm(`Tem certeza que deseja remover o layout "${agent?.name || layoutId}"?`)) return;
    try {
      await adminConfigService.deleteLabLayoutAgent(layoutId);
      setLabLayoutAgents(prev => {
        const next = { ...prev };
        delete next[layoutId];
        return next;
      });
      showSuccess('Layout removido com sucesso!');
    } catch (err) {
      setError('Erro ao remover layout: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleCreateLayoutAgent = async () => {
    if (!newLayoutForm?.id || !newLayoutForm?.name || !newLayoutForm?.user_prompt_template) {
      setError('Preencha ID, Nome e Prompt Template');
      return;
    }
    setIsSaving(true);
    try {
      const result = await adminConfigService.updateLabLayoutAgent(newLayoutForm.id, newLayoutForm);
      setLabLayoutAgents(prev => ({ ...prev, [newLayoutForm.id]: result.layout_agent }));
      setNewLayoutForm(null);
      showSuccess(`Layout "${newLayoutForm.name}" criado com sucesso!`);
    } catch (err) {
      setError('Erro ao criar layout: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsSaving(false);
    }
  };

  // ============================================
  // HANDLERS - LAB REACT AGENTS (provider react_render)
  // ============================================

  const handleSaveReactSystemPrompt = async () => {
    setIsSaving(true);
    try {
      await adminConfigService.updateLabReactSystemPrompt(labReactSystemPrompt);
      showSuccess('React system prompt atualizado com sucesso!');
    } catch (err) {
      setError('Erro ao salvar React system prompt: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveReactLayoutAgent = async (layoutId) => {
    setIsSaving(true);
    try {
      const agent = labReactLayoutAgents[layoutId];
      await adminConfigService.updateLabReactLayoutAgent(layoutId, agent);
      showSuccess(`React layout "${agent.name}" salvo com sucesso!`);
    } catch (err) {
      setError('Erro ao salvar React layout: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleReactLayoutAgent = async (layoutId, currentEnabled) => {
    try {
      await adminConfigService.toggleLabReactLayoutAgent(layoutId, !currentEnabled);
      setLabReactLayoutAgents(prev => ({
        ...prev,
        [layoutId]: { ...prev[layoutId], enabled: !currentEnabled }
      }));
      showSuccess(`React layout ${!currentEnabled ? 'ativado' : 'desativado'}!`);
    } catch (err) {
      setError('Erro ao alterar status do React layout');
    }
  };

  const handleDeleteReactLayoutAgent = async (layoutId) => {
    const agent = labReactLayoutAgents[layoutId];
    if (!window.confirm(`Remover o React layout "${agent?.name || layoutId}"?`)) return;
    try {
      await adminConfigService.deleteLabReactLayoutAgent(layoutId);
      setLabReactLayoutAgents(prev => {
        const next = { ...prev };
        delete next[layoutId];
        return next;
      });
      showSuccess('React layout removido com sucesso!');
    } catch (err) {
      setError('Erro ao remover React layout: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleCreateReactLayoutAgent = async () => {
    if (!newReactLayoutForm?.id || !newReactLayoutForm?.name || !newReactLayoutForm?.user_prompt_template) {
      setError('Preencha ID, Nome e Prompt Template');
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        ...newReactLayoutForm,
        needs_background_image: !!newReactLayoutForm.needs_background_image,
        supported_dimensions: newReactLayoutForm.supported_dimensions || ['square'],
      };
      const result = await adminConfigService.updateLabReactLayoutAgent(newReactLayoutForm.id, payload);
      setLabReactLayoutAgents(prev => ({ ...prev, [newReactLayoutForm.id]: result.layout_agent }));
      setNewReactLayoutForm(null);
      showSuccess(`React layout "${newReactLayoutForm.name}" criado com sucesso!`);
    } catch (err) {
      setError('Erro ao criar React layout: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsSaving(false);
    }
  };

  // ============================================
  // HANDLERS - LAB NICHOS
  // ============================================

  const handleSaveNiche = async (nicheId) => {
    const niche = labNiches[nicheId];
    if (!niche) return;
    if (!Array.isArray(niche.layout_agent_ids) || niche.layout_agent_ids.length !== 3) {
      setError('O nicho precisa de exatamente 3 layout_agent_ids');
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        name: niche.name || '',
        description: niche.description || '',
        icon: niche.icon || '',
        layout_agent_ids: niche.layout_agent_ids,
        react_layout_agent_ids: (niche.react_layout_agent_ids || []).filter(Boolean),
        tone_instructions: niche.tone_instructions || '',
        sample_prompts: niche.sample_prompts || [],
        default_colors: niche.default_colors || [],
        default_fonts: niche.default_fonts || {},
        enabled: niche.enabled !== false,
        ...(typeof niche.order === 'number' ? { order: niche.order } : {})
      };
      const result = await adminConfigService.updateLabNiche(nicheId, payload);
      setLabNiches(prev => ({ ...prev, [nicheId]: result.niche }));
      showSuccess(`Nicho "${niche.name}" salvo!`);
    } catch (err) {
      setError('Erro ao salvar nicho: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleNiche = async (nicheId, currentEnabled) => {
    try {
      await adminConfigService.toggleLabNiche(nicheId, !currentEnabled);
      setLabNiches(prev => ({
        ...prev,
        [nicheId]: { ...prev[nicheId], enabled: !currentEnabled }
      }));
      showSuccess(`Nicho ${!currentEnabled ? 'ativado' : 'desativado'}!`);
    } catch (err) {
      setError('Erro ao alterar status do nicho');
    }
  };

  const handleDeleteNiche = async (nicheId) => {
    const niche = labNiches[nicheId];
    if (!window.confirm(`Remover o nicho "${niche?.name || nicheId}"?`)) return;
    try {
      await adminConfigService.deleteLabNiche(nicheId);
      setLabNiches(prev => {
        const next = { ...prev };
        delete next[nicheId];
        return next;
      });
      showSuccess('Nicho removido!');
    } catch (err) {
      setError('Erro ao remover nicho: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleRegenerateNichePreviews = async (nicheId) => {
    setRegeneratingNicheId(nicheId);
    try {
      const result = await adminConfigService.regenerateLabNichePreviews(nicheId);
      setLabNiches(prev => ({ ...prev, [nicheId]: result.niche }));
      showSuccess(`${result.generated} previews gerados!`);
    } catch (err) {
      setError('Erro ao regerar previews: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRegeneratingNicheId(null);
    }
  };

  const handleCreateNiche = async () => {
    if (!newNicheForm?.id || !newNicheForm?.name) {
      setError('Preencha ID e Nome');
      return;
    }
    if (!Array.isArray(newNicheForm.layout_agent_ids) || newNicheForm.layout_agent_ids.length !== 3) {
      setError('Informe exatamente 3 layout_agent_ids');
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        name: newNicheForm.name,
        description: newNicheForm.description || '',
        icon: newNicheForm.icon || '',
        layout_agent_ids: newNicheForm.layout_agent_ids,
        tone_instructions: newNicheForm.tone_instructions || '',
        sample_prompts: newNicheForm.sample_prompts || [],
        default_colors: newNicheForm.default_colors || [],
        default_fonts: newNicheForm.default_fonts || {},
        enabled: true
      };
      const result = await adminConfigService.updateLabNiche(newNicheForm.id, payload);
      setLabNiches(prev => ({ ...prev, [newNicheForm.id]: result.niche }));
      setNewNicheForm(null);
      showSuccess(`Nicho "${newNicheForm.name}" criado!`);
    } catch (err) {
      setError('Erro ao criar nicho: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsSaving(false);
    }
  };

  // ============================================
  // HANDLERS - PLACID SETTINGS
  // ============================================

  const handleSavePlacidSettings = async () => {
    setIsSaving(true);
    try {
      await adminConfigService.updatePlacidSettings(placidSettings);
      showSuccess('Configurações do Placid salvas!');
    } catch (err) {
      setError('Erro ao salvar configurações do Placid');
    } finally {
      setIsSaving(false);
    }
  };

  // ============================================
  // HANDLERS - CREATOMATE SPLIT
  // ============================================

  const handleSaveCreatomateSplit = async () => {
    setIsSaving(true);
    try {
      await adminConfigService.updateCreatomateSplit(creatomateSplit);
      showSuccess('Configuração Creatomate salva!');
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setError('Erro ao salvar configuração Creatomate: ' + detail);
    } finally {
      setIsSaving(false);
    }
  };

  const handleProviderCountChange = (field, value) => {
    const total = placidSettings.templates_to_select || 3;
    const capped = Math.min(Math.max(0, value), total);
    setCreatomateSplit(prev => {
      // Os 4 fields, exceto o que esta sendo editado
      const all = ['placid_count', 'creatomate_count', 'templated_count', 'react_render_count'];
      const others = all.filter(f => f !== field);
      const remainder = total - capped;

      // Distribui o remainder entre os outros 3, preservando o maximo possivel dos valores antigos
      const prevValues = others.map(f => prev[f] || 0);
      const prevSum = prevValues.reduce((a, b) => a + b, 0);

      let newValues;
      if (prevSum === 0) {
        // Tudo no primeiro outro field
        newValues = [remainder, 0, 0];
      } else if (prevSum <= remainder) {
        // Adiciona o excesso no primeiro field
        newValues = [...prevValues];
        newValues[0] += remainder - prevSum;
      } else {
        // Reduz proporcionalmente (mantendo inteiros)
        newValues = prevValues.map(v => Math.floor((v / prevSum) * remainder));
        const diff = remainder - newValues.reduce((a, b) => a + b, 0);
        if (diff > 0) newValues[0] += diff;  // ajuste de arredondamento
      }

      const updated = { ...prev, [field]: capped };
      others.forEach((f, i) => { updated[f] = newValues[i]; });
      return updated;
    });
  };

  // ============================================
  // HANDLERS - TEMPLATED TEMPLATES
  // ============================================

  const handleLoadAvailableTemplated = async () => {
    setIsLoadingTemplated(true);
    try {
      const response = await adminConfigService.getAvailableTemplatedTemplates();
      setAvailableTemplatedTemplates(response.data || []);
    } catch (err) {
      setError('Erro ao carregar templates disponíveis');
    } finally {
      setIsLoadingTemplated(false);
    }
  };

  const handleImportTemplated = async (template) => {
    try {
      await adminConfigService.importTemplatedTemplate(template.id, {
        id: template.id,
        name: template.name,
        thumbnail: template.thumbnail,
        layers: template.layers || [],
        width: template.width,
        height: template.height,
        enabled: true
      });
      // Refresh
      const response = await adminConfigService.getTemplatedTemplates();
      setTemplatedTemplates(response.data || {});
      setSuccessMessage('Template importado com sucesso!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError('Erro ao importar template');
    }
  };

  const handleToggleTemplated = async (templateId, enabled) => {
    try {
      await adminConfigService.toggleTemplatedTemplate(templateId, enabled);
      setTemplatedTemplates(prev => ({
        ...prev,
        [templateId]: { ...prev[templateId], enabled }
      }));
    } catch (err) {
      setError('Erro ao atualizar template');
    }
  };

  const handleDeleteTemplated = async (templateId) => {
    if (!window.confirm('Remover este template?')) return;
    try {
      await adminConfigService.deleteTemplatedTemplate(templateId);
      setTemplatedTemplates(prev => {
        const updated = { ...prev };
        delete updated[templateId];
        return updated;
      });
    } catch (err) {
      setError('Erro ao remover template');
    }
  };

  // ============================================
  // HANDLERS - RESET
  // ============================================

  const handleResetConfig = async () => {
    if (!window.confirm('Tem certeza que deseja resetar TODAS as configurações para os valores padrão? Esta ação não pode ser desfeita.')) {
      return;
    }
    
    setIsSaving(true);
    try {
      await adminConfigService.resetConfig();
      await loadAllData();
      showSuccess('Configurações resetadas para valores padrão!');
    } catch (err) {
      setError('Erro ao resetar configurações');
    } finally {
      setIsSaving(false);
    }
  };

  // ============================================
  // RENDER - LOADING
  // ============================================

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-amber-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Carregando configurações...</p>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER - MAIN
  // ============================================

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white py-8 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-2">
            <div className="p-3 bg-amber-500/20 rounded-xl">
              <Shield className="w-8 h-8 text-amber-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Configurações do Sistema</h1>
              <p className="text-gray-400 mt-1">Painel de administração para configurar o motor de geração</p>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="max-w-7xl mx-auto px-6 pt-4">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="max-w-7xl mx-auto px-6 pt-4">
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl flex items-center gap-3">
            <Check className="w-5 h-5 flex-shrink-0" />
            <span>{successMessage}</span>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-8">

        {/* Tabs - 🆕 ADICIONADA TAB "Logs" */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            // Simplificação (2026-08): abas de funcionalidades desativadas ficam
            // OCULTAS (código preservado — basta re-adicionar a linha pra voltar):
            //   { id: 'modelSelector', label: 'Seletor IA', icon: Brain },        // só usado pela fallback chain do Placid
            //   { id: 'agent', label: 'Agente', icon: Bot },                       // agente Placid (desativado)
            //   { id: 'templatedTemplates', label: 'Templates Templated', icon: FileText }, // templated_count=0 em produção
            //   { id: 'fonts', label: 'Fontes', icon: Type },                      // read-only, mapeamento Placid
            { id: 'users', label: 'Usuários', icon: Users },
            { id: 'generators', label: 'Geradores de Imagem', icon: Image },
            { id: 'imagePrompt', label: 'Prompt de Imagem', icon: Bot },
            { id: 'templates', label: 'Distribuição de Posts', icon: Palette },
            { id: 'labAgents', label: 'Lab Agents', icon: Sparkles },
            { id: 'labReactAgents', label: 'Lab React Agents', icon: FlaskConical },
            { id: 'labNiches', label: 'Lab Nichos', icon: Shield },
            { id: 'logs', label: 'Logs de Falhas', icon: Activity },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-amber-500 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'users' && <UsersManagement />}

        {/* ============================================
            PROMPT DE IMAGEM (produção)
            O mesmo editor aparece no Lab em modo rascunho.
            ============================================ */}
        {activeTab === 'imagePrompt' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="mb-5">
              <h2 className="text-lg font-bold text-gray-900">Prompt de geração de imagens</h2>
              <p className="text-sm text-gray-500 mt-1">
                Instruções que o agente usa para planejar cada post antes de gerar a imagem.
              </p>
            </div>
            <ImagePromptEditor mode="production" />
          </div>
        )}

        {/* ============================================
            TAB: GENERATORS
            ============================================ */}
        {activeTab === 'generators' && (
          <div className="space-y-4">

            {/* ✅ Fase 4: Modelo de Imagem (Azure DALL-E vs OpenAI gpt-image) */}
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/40 rounded-2xl shadow-sm border border-emerald-200 overflow-hidden">
              <div className="p-5 border-b border-emerald-200">
                <h2 className="text-lg font-bold text-emerald-900 flex items-center gap-2">
                  ✨ Modelo de Imagem (Fase 4)
                </h2>
                <p className="text-emerald-800 text-sm mt-1">
                  Controla o provedor usado pelo motor <code className="bg-white px-1 rounded text-xs">gpt_image</code>.
                  Não afeta agentes Azure existentes (legenda, planner).
                </p>
              </div>
              <div className="p-5 space-y-4">

                <div>
                  <label className="block text-sm font-semibold text-emerald-900 mb-2">Provedor</label>
                  <div className="flex gap-2">
                    {[
                      { v: 'auto', label: 'Auto', desc: 'Routing automático' },
                      { v: 'openai', label: 'OpenAI', desc: 'gpt-image-* (caro)' },
                      { v: 'azure', label: 'Azure', desc: 'DALL-E (mais barato)' },
                    ].map(opt => (
                      <button
                        key={opt.v}
                        onClick={() => setImageModelProvider(prev => ({ ...prev, provider: opt.v }))}
                        className={`flex-1 p-3 rounded-xl border-2 text-left transition-all ${
                          imageModelProvider.provider === opt.v
                            ? 'border-emerald-500 bg-emerald-500 text-white shadow'
                            : 'border-emerald-200 bg-white text-emerald-700 hover:border-emerald-300'
                        }`}
                      >
                        <div className="font-bold text-sm">{opt.label}</div>
                        <div className={`text-xs mt-0.5 ${imageModelProvider.provider === opt.v ? 'text-white/80' : 'text-emerald-500'}`}>
                          {opt.desc}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-emerald-900 mb-2">
                    Override do modelo gpt-image
                  </label>
                  <select
                    value={imageModelProvider.gpt_image_model_override}
                    onChange={(e) => setImageModelProvider(prev => ({ ...prev, gpt_image_model_override: e.target.value }))}
                    className="w-full px-4 py-2 rounded-xl border-2 border-emerald-200 bg-white text-emerald-900 font-medium focus:border-emerald-500 focus:outline-none"
                  >
                    <option value="auto">auto (roteia por complexidade do texto)</option>
                    <option value="gpt-image-1">gpt-image-1 (mais barato, sem texto)</option>
                    <option value="gpt-image-1-mini">gpt-image-1-mini</option>
                    <option value="gpt-image-1.5">gpt-image-1.5 (texto curto)</option>
                    <option value="gpt-image-2">gpt-image-2 (melhor tipografia, texto longo)</option>
                    <option value="chatgpt-image-latest">chatgpt-image-latest</option>
                  </select>
                  <p className="text-xs text-emerald-700 mt-1">
                    Default <code>auto</code>: posts sem texto → gpt-image-1, texto curto → 1.5, texto longo → 2.
                  </p>
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    disabled={isSavingImageProvider}
                    onClick={async () => {
                      setIsSavingImageProvider(true);
                      try {
                        const res = await api.patch('/api/admin/config/image-model-provider', {
                          provider: imageModelProvider.provider,
                          gpt_image_model_override: imageModelProvider.gpt_image_model_override,
                        });
                        if (res.data?.data) {
                          setImageModelProvider({
                            provider: res.data.data.provider || 'auto',
                            gpt_image_model_override: res.data.data.gpt_image_model_override || 'auto',
                          });
                        }
                      } catch (err) {
                        alert('Erro ao salvar: ' + (err.response?.data?.detail || err.message));
                      } finally {
                        setIsSavingImageProvider(false);
                      }
                    }}
                    className="px-4 py-2 rounded-xl bg-emerald-500 text-white font-semibold hover:bg-emerald-600 disabled:bg-emerald-300 transition-colors"
                  >
                    {isSavingImageProvider ? 'Salvando…' : 'Salvar'}
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                  <Zap className="w-6 h-6 text-amber-500" />
                  Geradores de Imagem (Fallback Chain)
                </h2>
                <p className="text-gray-500 mt-1">
                  Configure a ordem de prioridade dos geradores. O primeiro ativo será usado primeiro.
                </p>
              </div>

              <div className="divide-y divide-gray-100">
                {generators.map((generator, index) => (
                  <div
                    key={generator.id}
                    className={`p-4 flex items-center gap-4 transition-colors ${
                      generator.enabled ? 'bg-white' : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => index > 0 && handleReorderGenerators(index, index - 1)}
                        disabled={index === 0}
                        className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => index < generators.length - 1 && handleReorderGenerators(index, index + 1)}
                        disabled={index === generators.length - 1}
                        className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>

                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      generator.enabled ? 'bg-amber-100 text-amber-700' : 'bg-gray-200 text-gray-500'
                    }`}>
                      {generator.order}
                    </div>

                    <span className="text-2xl">{generator.emoji || '🎨'}</span>

                    <div className="flex-1 min-w-0">
                      <h3 className={`font-semibold ${generator.enabled ? 'text-gray-900' : 'text-gray-500'}`}>
                        {generator.name}
                      </h3>
                      <p className="text-sm text-gray-500 truncate">{generator.description}</p>
                    </div>

                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      generator.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {generator.enabled ? 'Ativo' : 'Inativo'}
                    </span>

                    <button
                      onClick={() => handleToggleGenerator(generator.id, generator.enabled)}
                      className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                    >
                      {generator.enabled ? (
                        <ToggleRight className="w-8 h-8 text-green-500" />
                      ) : (
                        <ToggleLeft className="w-8 h-8 text-gray-400" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ============================================
            TAB: TEMPLATES
            ============================================ */}
        {activeTab === 'templates' && (
          <div className="space-y-6">
            {/* Placid Settings */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <Settings className="w-6 h-6 text-amber-500" />
                Configurações Gerais do Placid
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Templates a Selecionar
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={placidSettings.templates_to_select || 3}
                    onChange={(e) => setPlacidSettings(prev => ({
                      ...prev,
                      templates_to_select: parseInt(e.target.value)
                    }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gerações por Template
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="5"
                    value={placidSettings.generations_per_template || 1}
                    onChange={(e) => setPlacidSettings(prev => ({
                      ...prev,
                      generations_per_template: parseInt(e.target.value)
                    }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={handleSavePlacidSettings}
                    disabled={isSaving}
                    className="flex items-center gap-2 px-6 py-2 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-colors disabled:opacity-50"
                  >
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Salvar
                  </button>
                </div>
              </div>
            </div>

            {/* Provider Split Config */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <Zap className="w-6 h-6 text-purple-500" />
                Distribuição de Providers
              </h2>
              <p className="text-gray-500 text-sm mb-4">
                Ative para combinar Placid + Creatomate + Templated na geração de posts. A soma deve ser igual ao total de templates ({placidSettings.templates_to_select || 3}).
              </p>

              <div className="flex items-center gap-3 mb-4">
                <button
                  onClick={() => setCreatomateSplit(prev => ({ ...prev, enabled: !prev.enabled }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    creatomateSplit.enabled ? 'bg-purple-500' : 'bg-gray-300'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    creatomateSplit.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
                <span className="text-sm font-medium text-gray-700">
                  {creatomateSplit.enabled ? 'Ativado' : 'Desativado'}
                </span>
              </div>

              {creatomateSplit.enabled && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 items-end">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Posts via Placid
                      </label>
                      <input
                        type="number"
                        min="0"
                        max={placidSettings.templates_to_select || 3}
                        value={creatomateSplit.placid_count}
                        onChange={(e) => handleProviderCountChange('placid_count', parseInt(e.target.value) || 0)}
                        className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Posts via Creatomate
                      </label>
                      <input
                        type="number"
                        min="0"
                        max={placidSettings.templates_to_select || 3}
                        value={creatomateSplit.creatomate_count}
                        onChange={(e) => handleProviderCountChange('creatomate_count', parseInt(e.target.value) || 0)}
                        className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Posts via Templated.io
                      </label>
                      <input
                        type="number"
                        min="0"
                        max={placidSettings.templates_to_select || 3}
                        value={creatomateSplit.templated_count}
                        onChange={(e) => handleProviderCountChange('templated_count', parseInt(e.target.value) || 0)}
                        className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Posts via React Render
                      </label>
                      <input
                        type="number"
                        min="0"
                        max={placidSettings.templates_to_select || 3}
                        value={creatomateSplit.react_render_count || 0}
                        onChange={(e) => handleProviderCountChange('react_render_count', parseInt(e.target.value) || 0)}
                        className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <button
                        onClick={handleSaveCreatomateSplit}
                        disabled={isSaving}
                        className="flex items-center gap-2 px-6 py-2 bg-purple-500 text-white rounded-xl hover:bg-purple-600 transition-colors disabled:opacity-50"
                      >
                        {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Salvar
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <div className="flex gap-1">
                      {Array.from({ length: placidSettings.templates_to_select || 3 }).map((_, i) => {
                        const placidEnd = creatomateSplit.placid_count;
                        const creatomateEnd = placidEnd + creatomateSplit.creatomate_count;
                        const templatedEnd = creatomateEnd + (creatomateSplit.templated_count || 0);
                        let colorClass = 'bg-amber-400';
                        if (i >= placidEnd && i < creatomateEnd) {
                          colorClass = 'bg-purple-400';
                        } else if (i >= creatomateEnd && i < templatedEnd) {
                          colorClass = 'bg-pink-400';
                        } else if (i >= templatedEnd) {
                          colorClass = 'bg-blue-400';
                        }
                        return (
                          <div
                            key={i}
                            className={`w-8 h-2 rounded-full ${colorClass}`}
                          />
                        );
                      })}
                    </div>
                    <span>
                      {creatomateSplit.placid_count} Placid + {creatomateSplit.creatomate_count} Creatomate + {creatomateSplit.templated_count || 0} Templated + {creatomateSplit.react_render_count || 0} React = {(placidSettings.templates_to_select || 3)} total
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Templates List */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                    <Palette className="w-6 h-6 text-amber-500" />
                    Templates Disponíveis
                  </h2>
                  <p className="text-gray-500 mt-1">
                    {templates.filter(t => t.enabled).length} de {templates.length} templates ativos
                  </p>
                </div>
                
                <button
                  onClick={() => setIsImportModalOpen(true)}
                  className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 transition-colors shadow-sm"
                >
                  <Plus className="w-4 h-4" />
                  Importar do Placid
                </button>
              </div>

              {templates.length === 0 ? (
                <div className="p-12 text-center">
                  <Palette className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 mb-4">Nenhum template configurado</p>
                  <button
                    onClick={() => setIsImportModalOpen(true)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Importar Primeiro Template
                  </button>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {templates.map(template => (
                    <div
                      key={template.id}
                      className={`p-4 transition-colors ${
                        template.enabled ? 'bg-white' : 'bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className={`font-semibold ${template.enabled ? 'text-gray-900' : 'text-gray-500'}`}>
                              {template.title}
                            </h3>
                            <span className="text-xs text-gray-400 font-mono">({template.id})</span>
                          </div>
                          <p className="text-sm text-gray-500 line-clamp-1 mt-0.5">
                            {template.description}
                          </p>
                          <div className="flex gap-2 mt-2">
                            <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs rounded-full">
                              {template.picture_layers?.length || 0} imagens
                            </span>
                            <span className="px-2 py-0.5 bg-purple-50 text-purple-600 text-xs rounded-full">
                              {template.text_layers?.length || 0} textos
                            </span>
                            {template.uuid && (
                              <span className="px-2 py-0.5 bg-gray-50 text-gray-400 text-xs rounded-full font-mono">
                                {template.uuid}
                              </span>
                            )}
                          </div>
                        </div>

                        <span className={`px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                          template.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {template.enabled ? 'Ativo' : 'Inativo'}
                        </span>

                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => handleSyncLayers(template.id)}
                            disabled={syncingTemplateId === template.id}
                            className="p-2 hover:bg-blue-50 rounded-xl transition-colors group"
                            title="Sincronizar layers com Placid"
                          >
                            {syncingTemplateId === template.id ? (
                              <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                            ) : (
                              <RotateCw className="w-5 h-5 text-gray-400 group-hover:text-blue-500" />
                            )}
                          </button>

                          <button
                            onClick={() => handleToggleTemplate(template.id, template.enabled)}
                            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                            title={template.enabled ? 'Desativar' : 'Ativar'}
                          >
                            {template.enabled ? (
                              <ToggleRight className="w-7 h-7 text-green-500" />
                            ) : (
                              <ToggleLeft className="w-7 h-7 text-gray-400" />
                            )}
                          </button>

                          <button
                            onClick={() => handleDeleteTemplate(template.id, template.title)}
                            className="p-2 hover:bg-red-50 rounded-xl transition-colors group"
                            title="Remover template"
                          >
                            <Trash2 className="w-5 h-5 text-gray-400 group-hover:text-red-500" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ============================================
            TAB: MODEL SELECTOR
            ============================================ */}
        {activeTab === 'modelSelector' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                    <Brain className="w-6 h-6 text-amber-500" />
                    Seletor de Modelo por IA
                  </h2>
                  <p className="text-gray-500 mt-1">
                    Quando ativo, a IA analisa o prompt e escolhe o melhor modelo automaticamente.
                  </p>
                </div>
                
                <button
                  onClick={handleToggleModelSelector}
                  className={`flex items-center gap-3 px-6 py-3 rounded-xl font-semibold transition-all ${
                    modelSelector.enabled
                      ? 'bg-green-500 text-white hover:bg-green-600'
                      : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                  }`}
                >
                  {modelSelector.enabled ? (
                    <>
                      <ToggleRight className="w-6 h-6" />
                      Ativo
                    </>
                  ) : (
                    <>
                      <ToggleLeft className="w-6 h-6" />
                      Inativo
                    </>
                  )}
                </button>
              </div>

              {modelSelector.enabled && (
                <div className="space-y-4 border-t border-gray-100 pt-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Temperature</label>
                      <input
                        type="number"
                        min="0"
                        max="2"
                        step="0.1"
                        value={modelSelector.agent_config?.temperature || 0.1}
                        onChange={(e) => setModelSelector(prev => ({
                          ...prev,
                          agent_config: { ...prev.agent_config, temperature: parseFloat(e.target.value) }
                        }))}
                        className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Baixa (0.1) para respostas consistentes</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Max Tokens</label>
                      <input
                        type="number"
                        min="50"
                        max="500"
                        value={modelSelector.agent_config?.max_tokens || 100}
                        onChange={(e) => setModelSelector(prev => ({
                          ...prev,
                          agent_config: { ...prev.agent_config, max_tokens: parseInt(e.target.value) }
                        }))}
                        className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Poucos tokens para resposta rápida</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">System Prompt do Seletor</label>
                    <textarea
                      rows={10}
                      value={modelSelector.system_prompt || ''}
                      onChange={(e) => setModelSelector(prev => ({ ...prev, system_prompt: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 font-mono text-sm"
                      placeholder="Instruções para o seletor de modelo..."
                    />
                  </div>

                  <button
                    onClick={handleSaveModelSelectorConfig}
                    disabled={isSaving}
                    className="flex items-center gap-2 px-6 py-2 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-colors disabled:opacity-50"
                  >
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Salvar Configurações
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ============================================
            TAB: AGENT CONFIG
            ============================================ */}
        {activeTab === 'agent' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <Bot className="w-6 h-6 text-amber-500" />
                Parâmetros do Agente
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Temperature</label>
                  <input
                    type="number"
                    min="0"
                    max="2"
                    step="0.1"
                    value={agentConfig.agent_config?.temperature || 0.7}
                    onChange={(e) => setAgentConfig(prev => ({
                      ...prev,
                      agent_config: { ...prev.agent_config, temperature: parseFloat(e.target.value) }
                    }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Max Tokens</label>
                  <input
                    type="number"
                    min="500"
                    max="8000"
                    value={agentConfig.agent_config?.max_tokens || 2500}
                    onChange={(e) => setAgentConfig(prev => ({
                      ...prev,
                      agent_config: { ...prev.agent_config, max_tokens: parseInt(e.target.value) }
                    }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Max Iterações</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={agentConfig.agent_config?.max_iterations || 4}
                    onChange={(e) => setAgentConfig(prev => ({
                      ...prev,
                      agent_config: { ...prev.agent_config, max_iterations: parseInt(e.target.value) }
                    }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </div>

              <button
                onClick={handleSaveAgentConfig}
                disabled={isSaving}
                className="flex items-center gap-2 px-6 py-2 mt-4 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-colors disabled:opacity-50"
              >
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Salvar Parâmetros
              </button>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <FileText className="w-6 h-6 text-amber-500" />
                System Prompt do Agente
              </h2>
              
              <textarea
                rows={15}
                value={agentConfig.agent_system_prompt || ''}
                onChange={(e) => setAgentConfig(prev => ({ ...prev, agent_system_prompt: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 font-mono text-sm"
                placeholder="System prompt do agente..."
              />

              <div className="mt-4">
                <h3 className="font-medium text-gray-700 mb-2">User Prompt Template</h3>
                <textarea
                  rows={6}
                  value={agentConfig.agent_user_prompt_template || ''}
                  onChange={(e) => setAgentConfig(prev => ({ ...prev, agent_user_prompt_template: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 font-mono text-sm"
                  placeholder="Template do user prompt..."
                />
              </div>

              <button
                onClick={handleSaveAgentPrompts}
                disabled={isSaving}
                className="flex items-center gap-2 px-6 py-2 mt-4 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-colors disabled:opacity-50"
              >
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Salvar Prompts
              </button>
            </div>
          </div>
        )}

        {/* ============================================
            TAB: LAB AGENTS
            ============================================ */}
        {activeTab === 'labAgents' && (
          <div className="space-y-6">
            {/* Seção 1: Planner */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <Brain className="w-6 h-6 text-purple-500" />
                Agente Planejador
              </h2>
              <p className="text-gray-500 mb-4 text-sm">
                Planeja o lote de posts: define layout, textos e image_prompt para cada amostra.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Temperature</label>
                  <input
                    type="number" min="0" max="2" step="0.1"
                    value={labPlannerConfig.temperature || 0.7}
                    onChange={(e) => setLabPlannerConfig(prev => ({ ...prev, temperature: parseFloat(e.target.value) }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Max Tokens</label>
                  <input
                    type="number" min="500" max="3000"
                    value={labPlannerConfig.max_tokens || 1500}
                    onChange={(e) => setLabPlannerConfig(prev => ({ ...prev, max_tokens: parseInt(e.target.value) }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">System Prompt</label>
                <textarea
                  rows={5}
                  value={labPlannerConfig.system_prompt || ''}
                  onChange={(e) => setLabPlannerConfig(prev => ({ ...prev, system_prompt: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 font-mono text-sm"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">User Prompt Template</label>
                <textarea
                  rows={15}
                  value={labPlannerConfig.user_prompt_template || ''}
                  onChange={(e) => setLabPlannerConfig(prev => ({ ...prev, user_prompt_template: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 font-mono text-sm"
                />
              </div>

              {/* Variáveis disponíveis */}
              {labVariables.planner_variables && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-4">
                  <h4 className="font-medium text-purple-800 mb-2 text-sm">Variáveis disponíveis</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                    {Object.entries(labVariables.planner_variables).map(([key, desc]) => (
                      <div key={key} className="text-xs">
                        <code className="text-purple-700 font-bold">{key}</code>
                        <span className="text-gray-600 ml-1">— {desc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={handleSaveLabPlanner}
                disabled={isSaving}
                className="flex items-center gap-2 px-6 py-2 bg-purple-500 text-white rounded-xl hover:bg-purple-600 transition-colors disabled:opacity-50"
              >
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Salvar Planner
              </button>
            </div>

            {/* Seção 2: System Prompt Compartilhado */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <FileText className="w-6 h-6 text-purple-500" />
                System Prompt dos Layouts
              </h2>
              <p className="text-gray-500 mb-4 text-sm">
                Instruções técnicas compartilhadas por todos os layout specialists (regras Creatomate, cores, dimensões).
              </p>

              <textarea
                rows={20}
                value={labLayoutSystemPrompt}
                onChange={(e) => setLabLayoutSystemPrompt(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 font-mono text-sm"
              />

              <button
                onClick={handleSaveLabSystemPrompt}
                disabled={isSaving}
                className="flex items-center gap-2 px-6 py-2 mt-4 bg-purple-500 text-white rounded-xl hover:bg-purple-600 transition-colors disabled:opacity-50"
              >
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Salvar System Prompt
              </button>
            </div>

            {/* Seção 3: Layout Agents */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <Sparkles className="w-6 h-6 text-purple-500" />
                Agentes de Layout
              </h2>
              <p className="text-gray-500 mb-4 text-sm">
                Cada layout specialist gera o RenderScript Creatomate para um tipo de composição visual.
              </p>

              {/* Lista de layouts */}
              <div className="space-y-3">
                {Object.entries(labLayoutAgents)
                  .sort(([, a], [, b]) => (a.order || 999) - (b.order || 999))
                  .map(([layoutId, agent]) => (
                  <div key={layoutId} className="border border-gray-200 rounded-xl overflow-hidden">
                    {/* Header do layout */}
                    <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
                      <div
                        className="flex items-center gap-3 cursor-pointer flex-1"
                        onClick={() => setExpandedLayout(expandedLayout === layoutId ? null : layoutId)}
                      >
                        {expandedLayout === layoutId ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                        <div>
                          <span className="font-semibold text-gray-900">{agent.name}</span>
                          <span className="text-gray-400 text-sm ml-2">({layoutId})</span>
                          {agent.description && <p className="text-gray-500 text-xs mt-0.5">{agent.description}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleLayoutAgent(layoutId, agent.enabled)}
                          className="p-1"
                          title={agent.enabled ? 'Desativar' : 'Ativar'}
                        >
                          {agent.enabled ?
                            <ToggleRight className="w-8 h-8 text-green-500" /> :
                            <ToggleLeft className="w-8 h-8 text-gray-300" />
                          }
                        </button>
                        <button
                          onClick={() => handleDeleteLayoutAgent(layoutId)}
                          className="p-1 text-red-400 hover:text-red-600"
                          title="Excluir layout"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Conteúdo expandido */}
                    {expandedLayout === layoutId && (
                      <div className="p-4 border-t border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                            <input
                              type="text"
                              value={agent.name || ''}
                              onChange={(e) => setLabLayoutAgents(prev => ({
                                ...prev,
                                [layoutId]: { ...prev[layoutId], name: e.target.value }
                              }))}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                            <input
                              type="text"
                              value={agent.description || ''}
                              onChange={(e) => setLabLayoutAgents(prev => ({
                                ...prev,
                                [layoutId]: { ...prev[layoutId], description: e.target.value }
                              }))}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                            />
                          </div>
                        </div>

                        <label className="block text-sm font-medium text-gray-700 mb-1">User Prompt Template</label>
                        <textarea
                          rows={20}
                          value={agent.user_prompt_template || ''}
                          onChange={(e) => setLabLayoutAgents(prev => ({
                            ...prev,
                            [layoutId]: { ...prev[layoutId], user_prompt_template: e.target.value }
                          }))}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 font-mono text-sm"
                        />

                        {/* Variáveis disponíveis */}
                        {labVariables.layout_variables && (
                          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mt-3 mb-3">
                            <h4 className="font-medium text-purple-800 mb-2 text-sm">Variáveis disponíveis</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                              {Object.entries(labVariables.layout_variables).map(([key, desc]) => (
                                <div key={key} className="text-xs">
                                  <code className="text-purple-700 font-bold">{key}</code>
                                  <span className="text-gray-600 ml-1">— {desc}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <button
                          onClick={() => handleSaveLayoutAgent(layoutId)}
                          disabled={isSaving}
                          className="flex items-center gap-2 px-5 py-2 bg-purple-500 text-white rounded-xl hover:bg-purple-600 transition-colors disabled:opacity-50 text-sm"
                        >
                          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          Salvar Layout
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Form de novo layout */}
              {newLayoutForm ? (
                <div className="mt-4 border border-purple-200 rounded-xl p-4 bg-purple-50/50">
                  <h3 className="font-semibold text-gray-900 mb-3">Novo Layout Agent</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">ID (slug)</label>
                      <input
                        type="text"
                        placeholder="ex: quote"
                        value={newLayoutForm.id || ''}
                        onChange={(e) => setNewLayoutForm(prev => ({ ...prev, id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                      <input
                        type="text"
                        placeholder="ex: Quote"
                        value={newLayoutForm.name || ''}
                        onChange={(e) => setNewLayoutForm(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                      <input
                        type="text"
                        placeholder="ex: Fundo com citação em destaque"
                        value={newLayoutForm.description || ''}
                        onChange={(e) => setNewLayoutForm(prev => ({ ...prev, description: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prompt Template</label>
                  <textarea
                    rows={12}
                    placeholder="Monte um post Instagram 1080x1080 com layout...&#10;&#10;Use variáveis: {{handle}}, {{title}}, {{subtitle}}, {{primary_color}}, etc."
                    value={newLayoutForm.user_prompt_template || ''}
                    onChange={(e) => setNewLayoutForm(prev => ({ ...prev, user_prompt_template: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 font-mono text-sm"
                  />
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={handleCreateLayoutAgent}
                      disabled={isSaving}
                      className="flex items-center gap-2 px-5 py-2 bg-purple-500 text-white rounded-xl hover:bg-purple-600 transition-colors disabled:opacity-50 text-sm"
                    >
                      {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      Criar Layout
                    </button>
                    <button
                      onClick={() => setNewLayoutForm(null)}
                      className="px-5 py-2 text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 text-sm"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setNewLayoutForm({ id: '', name: '', description: '', user_prompt_template: '', enabled: true })}
                  className="flex items-center gap-2 mt-4 px-5 py-2 border-2 border-dashed border-purple-300 text-purple-600 rounded-xl hover:bg-purple-50 transition-colors text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Adicionar Layout
                </button>
              )}
            </div>
          </div>
        )}

        {/* ============================================
            TAB: LAB REACT AGENTS (provider react_render)
            ============================================ */}
        {activeTab === 'labReactAgents' && (
          <div className="space-y-6">
            {/* Sec 1: System prompt compartilhado */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                <FlaskConical className="w-6 h-6 text-blue-500" />
                React Render — System Prompt
              </h2>
              <p className="text-gray-500 mb-4 text-sm">
                Prompt compartilhado para o GPT-4.1 que gera React + Tailwind + Lucide.
                Aplica-se a todos os React Layout Agents abaixo.
              </p>
              <textarea
                rows={10}
                value={labReactSystemPrompt}
                onChange={(e) => setLabReactSystemPrompt(e.target.value)}
                placeholder="Voce e um designer e desenvolvedor frontend especializado..."
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              />
              <button
                onClick={handleSaveReactSystemPrompt}
                disabled={isSaving}
                className="mt-3 flex items-center gap-2 px-5 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors disabled:opacity-50 text-sm"
              >
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Salvar System Prompt
              </button>
            </div>

            {/* Sec 2: Lista de React Layout Agents */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                    <Sparkles className="w-6 h-6 text-blue-500" />
                    React Layout Agents
                  </h2>
                  <p className="text-gray-500 mt-1 text-sm">
                    Cada agent tem um prompt template, decide se gera background via Flux e quais dimensoes suporta.
                  </p>
                </div>
                <button
                  onClick={() => setNewReactLayoutForm({
                    id: '', name: '', description: '', user_prompt_template: '',
                    needs_background_image: false, supported_dimensions: ['square'], enabled: true,
                  })}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Novo Agent
                </button>
              </div>

              {Object.keys(labReactLayoutAgents).length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-sm">
                  Nenhum React layout agent configurado. Rode <code className="bg-gray-100 px-1 rounded">python -m scripts.seed_react_lab_agents</code>.
                </div>
              ) : (
                <div className="space-y-3">
                  {Object.entries(labReactLayoutAgents)
                    .sort(([, a], [, b]) => (a.order || 999) - (b.order || 999))
                    .map(([layoutId, agent]) => (
                      <div key={layoutId} className="border border-gray-200 rounded-xl overflow-hidden">
                        <div className="flex items-center justify-between p-4 bg-gray-50">
                          <button
                            onClick={() => setExpandedReactLayout(expandedReactLayout === layoutId ? null : layoutId)}
                            className="flex-1 text-left flex items-center gap-3"
                          >
                            {expandedReactLayout === layoutId ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                            <div>
                              <div className="font-semibold text-gray-900 text-sm flex items-center gap-2">
                                {agent.name || layoutId}
                                <span className="font-mono text-xs text-gray-400">({layoutId})</span>
                                {agent.needs_background_image && (
                                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">+BG</span>
                                )}
                                {(agent.supported_dimensions || ['square']).map(d => (
                                  <span key={d} className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">{d}</span>
                                ))}
                              </div>
                              <div className="text-xs text-gray-500">{agent.description || ''}</div>
                            </div>
                          </button>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleToggleReactLayoutAgent(layoutId, agent.enabled !== false)}
                              className="p-1"
                              title={agent.enabled !== false ? 'Desativar' : 'Ativar'}
                            >
                              {agent.enabled !== false ? <ToggleRight className="w-6 h-6 text-blue-500" /> : <ToggleLeft className="w-6 h-6 text-gray-400" />}
                            </button>
                            <button
                              onClick={() => handleDeleteReactLayoutAgent(layoutId)}
                              className="p-1 text-red-400 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {expandedReactLayout === layoutId && (
                          <div className="p-4 border-t border-gray-200">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                                <input
                                  type="text"
                                  value={agent.name || ''}
                                  onChange={(e) => setLabReactLayoutAgents(prev => ({
                                    ...prev,
                                    [layoutId]: { ...prev[layoutId], name: e.target.value }
                                  }))}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Descricao</label>
                                <input
                                  type="text"
                                  value={agent.description || ''}
                                  onChange={(e) => setLabReactLayoutAgents(prev => ({
                                    ...prev,
                                    [layoutId]: { ...prev[layoutId], description: e.target.value }
                                  }))}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                />
                              </div>
                            </div>

                            <div className="flex items-center gap-6 mb-4">
                              <label className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={!!agent.needs_background_image}
                                  onChange={(e) => setLabReactLayoutAgents(prev => ({
                                    ...prev,
                                    [layoutId]: { ...prev[layoutId], needs_background_image: e.target.checked }
                                  }))}
                                />
                                Precisa de background AI (Flux)
                              </label>
                              <div className="flex items-center gap-2 text-sm">
                                <span className="font-medium text-gray-700">Dimensoes:</span>
                                {['square', 'story'].map(d => {
                                  const supported = agent.supported_dimensions || ['square'];
                                  const isOn = supported.includes(d);
                                  return (
                                    <button
                                      key={d}
                                      onClick={() => {
                                        const next = isOn ? supported.filter(x => x !== d) : [...supported, d];
                                        setLabReactLayoutAgents(prev => ({
                                          ...prev,
                                          [layoutId]: { ...prev[layoutId], supported_dimensions: next.length ? next : ['square'] }
                                        }));
                                      }}
                                      className={`px-3 py-1 rounded-lg text-xs font-medium ${
                                        isOn ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'
                                      }`}
                                    >
                                      {d}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            <label className="block text-sm font-medium text-gray-700 mb-1">User Prompt Template</label>
                            <textarea
                              rows={20}
                              value={agent.user_prompt_template || ''}
                              onChange={(e) => setLabReactLayoutAgents(prev => ({
                                ...prev,
                                [layoutId]: { ...prev[layoutId], user_prompt_template: e.target.value }
                              }))}
                              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                            />

                            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-3 mb-3">
                              <h4 className="font-medium text-blue-800 mb-2 text-sm">Variaveis disponiveis</h4>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-1 text-xs">
                                {[
                                  ['{{title}}', 'Titulo planejado'],
                                  ['{{subtitle}}', 'Subtitulo'],
                                  ['{{cta}}', 'CTA puro'],
                                  ['{{cta_instruction}}', 'CTA formatado'],
                                  ['{{handle}}', '@marca'],
                                  ['{{brand_colors}}', 'Cores hex'],
                                  ['{{brand_image_url}}', 'URL da imagem AI'],
                                  ['{{width}}', 'Largura px'],
                                  ['{{height}}', 'Altura px'],
                                  ['{{lucide_icons_available}}', 'Whitelist de icones'],
                                  ['{{image_prompt}}', 'Prompt usado no Flux'],
                                ].map(([key, desc]) => (
                                  <div key={key}>
                                    <code className="text-blue-700 font-bold">{key}</code>
                                    <span className="text-gray-600 ml-1">— {desc}</span>
                                  </div>
                                ))}
                              </div>
                            </div>

                            <button
                              onClick={() => handleSaveReactLayoutAgent(layoutId)}
                              disabled={isSaving}
                              className="flex items-center gap-2 px-5 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 disabled:opacity-50 text-sm"
                            >
                              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                              Salvar Layout
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}

              {/* Form de novo agent */}
              {newReactLayoutForm && (
                <div className="mt-4 border-2 border-dashed border-blue-300 rounded-xl p-4 bg-blue-50">
                  <h4 className="font-bold text-blue-800 mb-3">Novo React Layout</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <input
                      type="text"
                      placeholder="ID slug (a-z, 0-9, _)"
                      value={newReactLayoutForm.id}
                      onChange={(e) => setNewReactLayoutForm(prev => ({ ...prev, id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') }))}
                      className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Nome"
                      value={newReactLayoutForm.name}
                      onChange={(e) => setNewReactLayoutForm(prev => ({ ...prev, name: e.target.value }))}
                      className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    />
                  </div>
                  <input
                    type="text"
                    placeholder="Descricao"
                    value={newReactLayoutForm.description}
                    onChange={(e) => setNewReactLayoutForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full mb-3 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                  <div className="flex items-center gap-6 mb-3 text-sm">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!newReactLayoutForm.needs_background_image}
                        onChange={(e) => setNewReactLayoutForm(prev => ({ ...prev, needs_background_image: e.target.checked }))}
                      />
                      Precisa de background AI
                    </label>
                    {['square', 'story'].map(d => {
                      const isOn = (newReactLayoutForm.supported_dimensions || []).includes(d);
                      return (
                        <button
                          key={d}
                          onClick={() => {
                            const cur = newReactLayoutForm.supported_dimensions || [];
                            const next = isOn ? cur.filter(x => x !== d) : [...cur, d];
                            setNewReactLayoutForm(prev => ({ ...prev, supported_dimensions: next }));
                          }}
                          className={`px-3 py-1 rounded-lg text-xs font-medium ${
                            isOn ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {d}
                        </button>
                      );
                    })}
                  </div>
                  <textarea
                    rows={12}
                    placeholder="Prompt template..."
                    value={newReactLayoutForm.user_prompt_template}
                    onChange={(e) => setNewReactLayoutForm(prev => ({ ...prev, user_prompt_template: e.target.value }))}
                    className="w-full mb-3 px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleCreateReactLayoutAgent}
                      disabled={isSaving}
                      className="flex items-center gap-2 px-5 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 disabled:opacity-50 text-sm"
                    >
                      {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      Criar
                    </button>
                    <button
                      onClick={() => setNewReactLayoutForm(null)}
                      className="px-5 py-2 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 text-sm"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ============================================
            TAB: LAB NICHOS
            ============================================ */}
        {activeTab === 'labNiches' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                <Shield className="w-6 h-6 text-indigo-500" />
                Nichos do Lab
              </h2>
              <p className="text-gray-500 mb-6 text-sm">
                Cada nicho define 3 layout agents, tom de texto, prompts de exemplo e cores/fontes padrão.
                Os previews cacheados aparecem na criação manual de posts.
              </p>

              <div className="space-y-3">
                {Object.values(labNiches).sort((a, b) => (a.order || 999) - (b.order || 999)).map(niche => {
                  const isExpanded = expandedNiche === niche.id;
                  const previews = niche.preview_samples || [];
                  const agentOptions = Object.keys(labLayoutAgents);
                  return (
                    <div key={niche.id} className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="flex items-center justify-between p-4 bg-gray-50">
                        <button
                          onClick={() => setExpandedNiche(isExpanded ? null : niche.id)}
                          className="flex items-center gap-3 flex-1 text-left"
                        >
                          <span className="text-2xl">{niche.icon || '🏷️'}</span>
                          <div>
                            <div className="font-semibold text-gray-900">{niche.name}</div>
                            <div className="text-xs text-gray-500">
                              {niche.id} · {(niche.layout_agent_ids || []).join(', ') || '(sem agents)'} · previews: {previews.length}
                            </div>
                          </div>
                        </button>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleRegenerateNichePreviews(niche.id)}
                            disabled={regeneratingNicheId === niche.id}
                            className="flex items-center gap-1 text-xs px-3 py-1.5 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 disabled:opacity-50"
                            title="Regerar previews"
                          >
                            {regeneratingNicheId === niche.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />}
                            Previews
                          </button>
                          <button
                            onClick={() => handleToggleNiche(niche.id, niche.enabled !== false)}
                            className="text-gray-600 hover:text-indigo-600"
                            title={niche.enabled === false ? 'Ativar' : 'Desativar'}
                          >
                            {niche.enabled === false ? <ToggleLeft className="w-6 h-6" /> : <ToggleRight className="w-6 h-6 text-green-500" />}
                          </button>
                          <button onClick={() => handleDeleteNiche(niche.id)} className="text-gray-400 hover:text-red-600" title="Remover">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="p-4 space-y-4 bg-white">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Nome</label>
                              <input
                                type="text"
                                value={niche.name || ''}
                                onChange={(e) => setLabNiches(prev => ({ ...prev, [niche.id]: { ...prev[niche.id], name: e.target.value } }))}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Ícone (emoji)</label>
                              <input
                                type="text"
                                value={niche.icon || ''}
                                onChange={(e) => setLabNiches(prev => ({ ...prev, [niche.id]: { ...prev[niche.id], icon: e.target.value } }))}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Ordem</label>
                              <input
                                type="number"
                                value={niche.order || 0}
                                onChange={(e) => setLabNiches(prev => ({ ...prev, [niche.id]: { ...prev[niche.id], order: parseInt(e.target.value) || 0 } }))}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Descrição</label>
                            <input
                              type="text"
                              value={niche.description || ''}
                              onChange={(e) => setLabNiches(prev => ({ ...prev, [niche.id]: { ...prev[niche.id], description: e.target.value } }))}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Layout Agents Creatomate (exatamente 3)</label>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                              {[0, 1, 2].map(i => (
                                <select
                                  key={i}
                                  value={(niche.layout_agent_ids || [])[i] || ''}
                                  onChange={(e) => {
                                    const ids = [...(niche.layout_agent_ids || ['', '', ''])];
                                    ids[i] = e.target.value;
                                    setLabNiches(prev => ({ ...prev, [niche.id]: { ...prev[niche.id], layout_agent_ids: ids } }));
                                  }}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                >
                                  <option value="">— Agent {i + 1} —</option>
                                  {agentOptions.map(id => (
                                    <option key={id} value={id}>{labLayoutAgents[id]?.name || id}</option>
                                  ))}
                                  {/* Permite manter ID textual ainda não existente */}
                                  {(niche.layout_agent_ids || [])[i] && !agentOptions.includes((niche.layout_agent_ids || [])[i]) && (
                                    <option value={(niche.layout_agent_ids || [])[i]}>
                                      {(niche.layout_agent_ids || [])[i]} (não existe)
                                    </option>
                                  )}
                                </select>
                              ))}
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Layout Agents React Render (opcional, usados quando admin configura react_render_count {'>'} 0)
                            </label>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                              {[0, 1, 2].map(i => {
                                const reactOptions = Object.keys(labReactLayoutAgents);
                                const currentVal = (niche.react_layout_agent_ids || [])[i] || '';
                                return (
                                  <select
                                    key={i}
                                    value={currentVal}
                                    onChange={(e) => {
                                      const ids = [...(niche.react_layout_agent_ids || [])];
                                      while (ids.length <= i) ids.push('');
                                      ids[i] = e.target.value;
                                      // Remove vazios do final
                                      while (ids.length > 0 && !ids[ids.length - 1]) ids.pop();
                                      setLabNiches(prev => ({ ...prev, [niche.id]: { ...prev[niche.id], react_layout_agent_ids: ids } }));
                                    }}
                                    className="w-full px-3 py-2 border border-blue-200 bg-blue-50/30 rounded-lg text-sm"
                                  >
                                    <option value="">— React Agent {i + 1} (opcional) —</option>
                                    {reactOptions.map(id => (
                                      <option key={id} value={id}>{labReactLayoutAgents[id]?.name || id}</option>
                                    ))}
                                    {currentVal && !reactOptions.includes(currentVal) && (
                                      <option value={currentVal}>{currentVal} (não existe)</option>
                                    )}
                                  </select>
                                );
                              })}
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Tone instructions</label>
                            <textarea
                              rows={4}
                              value={niche.tone_instructions || ''}
                              onChange={(e) => setLabNiches(prev => ({ ...prev, [niche.id]: { ...prev[niche.id], tone_instructions: e.target.value } }))}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Sample prompts (um por linha)</label>
                            <textarea
                              rows={4}
                              value={(niche.sample_prompts || []).join('\n')}
                              onChange={(e) => setLabNiches(prev => ({ ...prev, [niche.id]: { ...prev[niche.id], sample_prompts: e.target.value.split('\n').filter(l => l.trim()) } }))}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono"
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Cores padrão (hex, separadas por vírgula)</label>
                              <input
                                type="text"
                                value={(niche.default_colors || []).join(', ')}
                                onChange={(e) => setLabNiches(prev => ({ ...prev, [niche.id]: { ...prev[niche.id], default_colors: e.target.value.split(',').map(s => s.trim()).filter(Boolean) } }))}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Fonte primária</label>
                                <input
                                  type="text"
                                  value={niche.default_fonts?.primary || ''}
                                  onChange={(e) => setLabNiches(prev => ({ ...prev, [niche.id]: { ...prev[niche.id], default_fonts: { ...(prev[niche.id].default_fonts || {}), primary: e.target.value } } }))}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Fonte secundária</label>
                                <input
                                  type="text"
                                  value={niche.default_fonts?.secondary || ''}
                                  onChange={(e) => setLabNiches(prev => ({ ...prev, [niche.id]: { ...prev[niche.id], default_fonts: { ...(prev[niche.id].default_fonts || {}), secondary: e.target.value } } }))}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                />
                              </div>
                            </div>
                          </div>

                          {previews.length > 0 && (
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-2">Previews cacheados</label>
                              <div className="grid grid-cols-3 gap-2">
                                {previews.map((p, idx) => (
                                  <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
                                    {p.image_url ? (
                                      <img src={p.image_url} alt={p.title || `Preview ${idx + 1}`} className="w-full h-32 object-cover" />
                                    ) : (
                                      <div className="w-full h-32 bg-gray-100 flex items-center justify-center text-gray-400 text-xs">sem imagem</div>
                                    )}
                                    <div className="p-2 text-xs text-gray-500 truncate">{p.layout_agent_id || '—'}</div>
                                  </div>
                                ))}
                              </div>
                              {niche.preview_generated_at && (
                                <p className="text-xs text-gray-400 mt-2">Gerado em: {niche.preview_generated_at}</p>
                              )}
                            </div>
                          )}

                          <div className="flex gap-2 pt-2">
                            <button
                              onClick={() => handleSaveNiche(niche.id)}
                              disabled={isSaving}
                              className="flex items-center gap-2 px-5 py-2 bg-indigo-500 text-white rounded-xl hover:bg-indigo-600 text-sm disabled:opacity-50"
                            >
                              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                              Salvar Nicho
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {newNicheForm ? (
                <div className="mt-6 border-2 border-dashed border-indigo-300 rounded-xl p-4 bg-indigo-50">
                  <h3 className="font-semibold text-gray-900 mb-3">Novo nicho</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">ID (slug)</label>
                      <input
                        type="text"
                        value={newNicheForm.id}
                        onChange={(e) => setNewNicheForm(prev => ({ ...prev, id: e.target.value }))}
                        placeholder="ex: advogados"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Nome</label>
                      <input
                        type="text"
                        value={newNicheForm.name}
                        onChange={(e) => setNewNicheForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Advogados"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
                    {[0, 1, 2].map(i => (
                      <input
                        key={i}
                        type="text"
                        value={newNicheForm.layout_agent_ids?.[i] || ''}
                        onChange={(e) => {
                          const ids = [...(newNicheForm.layout_agent_ids || ['', '', ''])];
                          ids[i] = e.target.value;
                          setNewNicheForm(prev => ({ ...prev, layout_agent_ids: ids }));
                        }}
                        placeholder={`Layout agent ${i + 1}`}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono"
                      />
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleCreateNiche}
                      disabled={isSaving}
                      className="flex items-center gap-2 px-5 py-2 bg-indigo-500 text-white rounded-xl hover:bg-indigo-600 text-sm disabled:opacity-50"
                    >
                      {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      Criar Nicho
                    </button>
                    <button
                      onClick={() => setNewNicheForm(null)}
                      className="px-5 py-2 text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 text-sm"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setNewNicheForm({ id: '', name: '', icon: '', description: '', layout_agent_ids: ['', '', ''], tone_instructions: '', sample_prompts: [], default_colors: [], default_fonts: {} })}
                  className="flex items-center gap-2 mt-4 px-5 py-2 border-2 border-dashed border-indigo-300 text-indigo-600 rounded-xl hover:bg-indigo-50 transition-colors text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Adicionar Nicho
                </button>
              )}
            </div>
          </div>
        )}

        {/* ============================================
            TAB: FONTS
            ============================================ */}
        {activeTab === 'fonts' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <Type className="w-6 h-6 text-amber-500" />
              Fontes Populares
            </h2>
            <p className="text-gray-500 mb-6">
              Mapeamento de nomes amigáveis para códigos de fonte do Placid.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[500px] overflow-y-auto">
              {Object.entries(fonts).map(([name, code]) => (
                <div key={name} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <span className="font-medium text-gray-900">{name}</span>
                  <span className="text-gray-400">→</span>
                  <span className="text-xs text-gray-500 font-mono truncate flex-1">{code}</span>
                </div>
              ))}
            </div>
            
            <p className="text-sm text-gray-500 mt-4">
              Total: {Object.keys(fonts).length} fontes configuradas
            </p>
          </div>
        )}

        {/* ============================================
            TAB: TEMPLATED TEMPLATES
            ============================================ */}
        {activeTab === 'templatedTemplates' && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                  <FileText className="w-6 h-6 text-blue-500" />
                  Templates Templated.io
                </h2>
                <button
                  onClick={handleLoadAvailableTemplated}
                  disabled={isLoadingTemplated}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors disabled:opacity-50"
                >
                  {isLoadingTemplated ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Importar Templates
                </button>
              </div>

              {/* Available templates for import */}
              {availableTemplatedTemplates.length > 0 && (
                <div className="mb-6 p-4 bg-blue-50 rounded-xl">
                  <h3 className="font-medium text-blue-900 mb-3">Templates disponíveis para importar:</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {availableTemplatedTemplates.map(t => (
                      <div key={t.id} className="bg-white p-3 rounded-lg border border-blue-200 flex flex-col items-center gap-2">
                        {t.thumbnail && <img src={t.thumbnail} alt={t.name} className="w-full h-20 object-cover rounded" />}
                        <span className="text-xs font-medium text-gray-700 text-center">{t.name || t.id}</span>
                        <button
                          onClick={() => handleImportTemplated(t)}
                          className="text-xs px-3 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                        >
                          Importar
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Configured templates list */}
              {Object.keys(templatedTemplates).length === 0 ? (
                <p className="text-gray-500 text-sm">Nenhum template configurado. Clique em "Importar Templates" para começar.</p>
              ) : (
                <div className="space-y-3">
                  {Object.entries(templatedTemplates).map(([tid, t]) => (
                    <div key={tid} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        {t.thumbnail && <img src={t.thumbnail} alt={t.name} className="w-12 h-12 object-cover rounded-lg" />}
                        <div>
                          <span className="font-medium text-gray-900">{t.name || tid}</span>
                          <span className="text-xs text-gray-500 ml-2">
                            {(t.layers?.length || 0)} layers
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleToggleTemplated(tid, !t.enabled)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${t.enabled ? 'bg-blue-500' : 'bg-gray-300'}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${t.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                        <button
                          onClick={() => handleDeleteTemplated(tid)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ============================================
            TAB: LOGS DE FALHAS
            ============================================ */}
        {activeTab === 'logs' && (
          <ModelFailuresPanel adminConfigService={adminConfigService} />
        )}
      </div>

      {/* Modal de Importação */}
      <PlacidTemplateImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImported={handleTemplateImported}
      />
    </div>
  );
};

export default AdminConfigPage;